import { Elements } from "@stripe/react-stripe-js";
import { useQuery } from "@tanstack/react-query";
import { env } from "config/env";
import React from "react";
import type { PaymentSourceId } from "./useSaveStripePaymentMethod";
import { useStripePaymentMethodUpdateContainer } from "./useStripePaymentMethodUpdateContainer";

type StripeElementsProviderProps = {
  sourceId: PaymentSourceId;
  className?: string;
  /** Shown while the SetupIntent loads; a spinner by default. */
  fallback?: React.ReactNode;
  /** Shown if the SetupIntent can't be created; without it the fallback stays up. */
  errorFallback?: React.ReactNode;
  children: React.ReactNode;
};

const stripeQuery = {
  queryKey: ["stripe-js"],
  queryFn: () => import("@stripe/stripe-js").then(({ loadStripe }) => loadStripe(env.REACT_APP_STRIPE_KEY)),
  staleTime: Infinity,
  retry: false,
};

/** Stripe Elements bound to a SetupIntent for the location's or organization's own customer. */
export function StripeElementsProvider({
  sourceId,
  className,
  fallback,
  errorFallback,
  children,
}: Readonly<StripeElementsProviderProps>) {
  const stripe = useQuery(stripeQuery);
  const { isLoading, isError, stripeStyles, clientSecret } = useStripePaymentMethodUpdateContainer({ sourceId });

  if ((isError || stripe.isError) && errorFallback) return errorFallback;
  return (
    <div className={className}>
      {isLoading ? (
        (fallback ?? (
          <div className="flex w-full justify-center">
            <div className="loading-spinner size-8 text-brand" />
          </div>
        ))
      ) : (
        <Elements options={{ clientSecret, ...stripeStyles }} stripe={stripe.data ?? null}>
          {children}
        </Elements>
      )}
    </div>
  );
}
